import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DB } from '@services/database.service';
import { supabase } from '@/supabaseClient';
import { useUIStore } from '@stores/v4/useUIStore';
import { useHistoryStore } from './useHistoryStore';
import { SleepLog } from '@/types/zod.schemas';

// NOTE: No circular imports here at top level.

interface RecoverySession {
    startTime: string;
    p_consume_event_id: string | null;
}

interface RecoveryState {
    activeRecoverySession: RecoverySession | null;
    recoveryHistory: SleepLog[];
}

interface RecoveryActions {
    startRecovery: (consumeEventId?: string | null) => void;
    endRecovery: () => Promise<void>;
    fetchRecoveryHistory: () => Promise<void>;
    deleteRecoveryLog: (logId: string) => Promise<void>;
    updateRecoveryLog: (logId: string, payload: any) => Promise<void>;
    resetRecovery: () => void;
}

export const useRecoveryStore = create<RecoveryState & RecoveryActions>()(
    persist(
        (set, get) => ({
            activeRecoverySession: null,
            recoveryHistory: [],

            startRecovery: (consumeEventId = null) => {
                set({ activeRecoverySession: { 
                    startTime: new Date().toISOString(),
                    p_consume_event_id: consumeEventId 
                } });
            },

            endRecovery: async () => {
                const session = get().activeRecoverySession;
                if (!session) {
                    console.warn("[RecoveryStore] No active recovery session found to end.");
                    return;
                }

                useUIStore.getState().setIsLoading(true);
                try {
                    const { data: userData, error: authError } = await supabase.auth.getUser();
                    if (authError) throw authError;
                    const user = userData.user;
                    if (!user) throw new Error("User not authenticated");

                    const payload = {
                        user_id: user.id,
                        start_time: session.startTime,
                        end_time: new Date().toISOString()
                    };

                    const { data, error } = await DB.insertSleepLog(payload);
                    if (error) throw error;
                    if (!data) throw new Error("Failed to insert sleep log");

                    const { error: regError } = await DB.registerRecoveryEvent(data.id, session.p_consume_event_id || null);
                    if (regError) {
                        console.error("[RecoveryStore] registerRecoveryEvent FAILED:", regError);
                    }

                    set({ activeRecoverySession: null });
                    
                    await Promise.all([
                        get().fetchRecoveryHistory(),
                        useHistoryStore.getState().fetchGlobalHistory(true)
                    ]);
                } catch (err: any) {
                    console.error("[RecoveryStore] endRecovery FAILED:", err);
                    throw err; 
                } finally {
                    useUIStore.getState().setIsLoading(false);
                }
            },

            fetchRecoveryHistory: async () => {
                try {
                    const { data: userData } = await supabase.auth.getUser();
                    const user = userData.user;
                    const { data } = await DB.fetchSleepHistory(user?.id || '');
                    set({ recoveryHistory: (data || []) as any });
                } catch (err) {
                    console.error("[RecoveryStore] fetchRecoveryHistory FAILED:", err);
                }
            },

            deleteRecoveryLog: async (logId) => {
                useUIStore.getState().setIsLoading(true);
                try {
                    await DB.deleteSleepLog(logId);
                    await get().fetchRecoveryHistory();
                } catch (err) {
                    console.error("[RecoveryStore] deleteRecoveryLog FAILED:", err);
                } finally {
                    useUIStore.getState().setIsLoading(false);
                }
            },

            updateRecoveryLog: async (logId, payload) => {
                useUIStore.getState().setIsLoading(true);
                try {
                    await DB.updateSleepLog(logId, payload);
                    await DB.registerRecoveryEvent(logId);
                    await get().fetchRecoveryHistory();
                } catch (err) {
                    console.error("[RecoveryStore] updateRecoveryLog FAILED:", err);
                } finally {
                    useUIStore.getState().setIsLoading(false);
                }
            },

            resetRecovery: () => set({ activeRecoverySession: null })
        }),
        { 
            name: 'mp-recovery-session',
            partialize: (state) => ({ activeRecoverySession: state.activeRecoverySession })
        }
    )
);

if (typeof window !== 'undefined') {
    (window as any).useRecoveryStore = useRecoveryStore;
}
